import mongoose from "mongoose";
import cron from "node-cron";

import { ATTENDANCE_LOG_TYPE, AttendanceLog } from "../models";
import { Class } from "../models/class";
import { getClassSessionForDate } from "../utils/class-session-utils";
import { logger } from "../utils/logger";

/**
 * Service to automatically check out students who forgot to check out when the class ends.
 * Runs periodically and sets check-out time to the class end time for the session.
 */
class StudentAutoCheckoutService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private cronJob: any = null;

  /**
   * Start the auto check-out cron job.
   * Runs every 5 minutes to find students checked in without check-out past class end.
   */
  start(): void {
    if (this.cronJob) {
      logger.warn("Student auto check-out service is already running");
      return;
    }

    this.cronJob = cron.schedule("*/5 * * * *", async () => {
      await this.processAutoCheckouts();
    });

    logger.info("Student auto check-out service started");
  }

  /**
   * Stop the auto check-out cron job.
   */
  stop(): void {
    if (this.cronJob) {
      this.cronJob.stop();
      this.cronJob = null;
      logger.info("Student auto check-out service stopped");
    }
  }

  /**
   * Find student check-ins without a check-out where class end time has passed,
   * and create check-out logs with timestamp = class end time.
   * Only the latest check-in per (userId, classId) without a check-out is considered.
   */
  private async processAutoCheckouts(): Promise<void> {
    try {
      const now = new Date();
      const studentCheckIns = await AttendanceLog.find({
        userRole: "student",
        logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
        classId: { $exists: true, $ne: null },
      })
        .sort({ timestamp: -1 })
        .lean();

      // Group by userId+classId and keep only latest check-in per group
      const latestByUserClass = new Map<string, (typeof studentCheckIns)[0]>();
      for (const checkIn of studentCheckIns) {
        const cid = checkIn.classId;
        if (cid == undefined) continue;
        const key = `${checkIn.userId.toString()}-${cid.toString()}`;
        if (!latestByUserClass.has(key)) {
          latestByUserClass.set(key, checkIn);
        }
      }

      let autoCheckOutCount = 0;
      for (const checkIn of latestByUserClass.values()) {
        const userId = checkIn.userId;
        const classId = checkIn.classId;
        if (classId == undefined) continue;
        const checkInTime = new Date(checkIn.timestamp);

        const hasCheckOut = await AttendanceLog.exists({
          userId,
          classId,
          userRole: "student",
          logType: ATTENDANCE_LOG_TYPE.CHECK_OUT,
          timestamp: { $gt: checkInTime },
        });
        if (hasCheckOut) continue;

        const classItem = await Class.findById(classId).lean();
        if (!classItem) continue;

        const session = getClassSessionForDate(classItem, checkInTime);
        if (!session) continue;

        if (now <= session.sessionEnd) continue;

        const checkoutTime = new Date(session.sessionEnd);
        await AttendanceLog.create({
          schoolId: checkIn.schoolId,
          classId: checkIn.classId,
          semesterId: checkIn.semesterId,
          userId: checkIn.userId,
          userRole: "student",
          logType: ATTENDANCE_LOG_TYPE.CHECK_OUT,
          timestamp: checkoutTime,
        });
        autoCheckOutCount++;
        logger.info("Auto check-out applied", {
          userId: userId.toString(),
          classId: classId.toString(),
          checkInTime,
          checkOutTime: checkoutTime,
        });
      }

      if (autoCheckOutCount > 0) {
        logger.info(
          `Student auto check-out: ${autoCheckOutCount} record(s) processed`,
        );
      }
    } catch (error) {
      logger.error("Student auto check-out job error:", error);
    }
  }
}

export const studentAutoCheckoutService = new StudentAutoCheckoutService();
